/**
 * Fuel Level: field + manual picker dialog (patrol logout / FSD)
 * ==============================================================
 * Two pieces, matching how the parked location works:
 *
 *   FuelLevelField   the row inside the form — the level that's been set, and
 *                    the button for setting it
 *   FuelLevelDialog  the popup that button opens, one tap per level,
 *                    rendered as a sibling of the form's own dialog
 *
 * Patrol logout and FSD both read what the vehicle reports over Geotab and
 * offer it as a one-tap confirm. Each opts in with allowVehicleReading — the
 * prop is off by default so a new call site can't pick the reading up by
 * accident.
 *
 * Either way the reading is never written on its own: the recorded level is
 * whatever was selected, confirmed or overridden.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Fuel, RefreshCw, Check } from "lucide-react";

export const FUEL_LEVELS = ["Full", "7/8", "3/4", "5/8", "1/2", "3/8", "1/4", "1/8", "Empty"];

// Same reading the dropdown used to give: low is red, getting low is amber.
const levelTone = (level) => {
  if (level === "Empty" || level === "1/8") return "red";
  if (level === "1/4" || level === "3/8") return "amber";
  return "green";
};

const TONE_SELECTED = {
  red: "bg-red-600 border-red-600 text-white",
  amber: "bg-amber-600 border-amber-600 text-white",
  green: "bg-green-600 border-green-600 text-white",
};

const TONE_IDLE = {
  red: "border-red-500/60 text-red-600 dark:text-red-400 hover:border-red-500",
  amber: "border-amber-500/60 text-amber-600 dark:text-amber-400 hover:border-amber-500",
  green: "border-green-500/60 text-green-600 dark:text-green-400 hover:border-green-500",
};

export function FuelLevelField({
  carId,
  value,
  onChange,
  onManual,
  label = "Fuel Level *",
  // Offer the vehicle's own reading to confirm. Off by default — patrol
  // logout and FSD opt in.
  allowVehicleReading = false,
}) {
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);

  // Read once per dialog — the field mounts when the dialog opens.
  useEffect(() => {
    if (!allowVehicleReading || !carId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/patrol/cars/${carId}/fuel-reading`,
          { withCredentials: true }
        );
        if (!cancelled) setReading(res.data);
      } catch (e) {
        if (!cancelled) setReading({ available: false, reason: "Could not read the vehicle's fuel" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [allowVehicleReading, carId]);

  const confirmed = reading?.available && value === reading.level;

  return (
    <div>
      <Label>{label}</Label>

      {loading && (
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Checking the vehicle's fuel...
        </p>
      )}

      {!loading && reading?.available && (!value || confirmed) && (
        <div className={`mt-1 p-2 rounded border ${
          confirmed
            ? "border-green-500 bg-green-100 dark:bg-green-900/20"
            : "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800"
        }`}>
          <div className="flex items-center gap-2">
            <Fuel className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
            <span className="flex-1 text-sm text-slate-800 dark:text-slate-200">
              Vehicle reports <span className="font-bold">{reading.level}</span>
              <span className="text-slate-500"> ({Math.round(reading.percent)}%)</span>
            </span>
            {confirmed ? (
              <span className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1 flex-shrink-0">
                <Check className="w-3 h-3" />Confirmed
              </span>
            ) : (
              <Button type="button" size="sm" onClick={() => onChange(reading.level)} className="btn-primary !h-6 !min-h-0 text-[10px] px-2 flex-shrink-0">
                Confirm
              </Button>
            )}
          </div>
        </div>
      )}

      {!loading && reading && !reading.available && reading.reason && (
        <p className="mt-1 text-[11px] text-slate-500">{reading.reason} — set it manually.</p>
      )}

      {/* A level chosen by hand rather than off the vehicle — settled either
          way, so it reads the same green as a confirmed reading. */}
      {value && !confirmed && (
        <div className="mt-1 flex items-center gap-2 p-2 rounded border border-green-500 bg-green-100 dark:bg-green-900/20">
          <Fuel className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
          <span className="flex-1 text-sm text-slate-800 dark:text-slate-200">
            Set to <span className="font-bold">{value}</span>
          </span>
          <span className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1 flex-shrink-0">
            <Check className="w-3 h-3" />Set
          </span>
        </div>
      )}

      <Button type="button" variant="outline" onClick={onManual} className="w-full mt-2">
        <Fuel className="w-4 h-4 mr-2" />{value ? "Change Fuel Level" : "Select Fuel Level"}
      </Button>
    </div>
  );
}

export function FuelLevelDialog({ open, onOpenChange, value, onChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-slate-800"
        // Nothing here takes typing, so don't let it pull up a keyboard.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader><DialogTitle>Select Fuel Level</DialogTitle></DialogHeader>
        {/* Every level one tap away, rather than a dropdown someone has to
            scroll while standing at the car. */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          {FUEL_LEVELS.map((level) => {
            const tone = levelTone(level);
            const selected = value === level;
            return (
              <button
                key={level}
                type="button"
                onClick={() => { onChange(level); onOpenChange(false); }}
                className={`h-14 rounded border text-sm font-bold transition-colors ${
                  selected ? TONE_SELECTED[tone] : `bg-white dark:bg-slate-900 ${TONE_IDLE[tone]}`
                }`}
              >
                {level}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
